import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AutoCompleteModule,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent
} from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PromotionService } from '../promotion.service';
import { GroupService } from '../../groups/group.service';
import { ProductService } from '../../../services/product.service';
import { SavePromotionPayload } from '../../../models/promotion.model';
import { Group } from '../../../models/group.model';
import { Product } from '../../../models/product.model';

export interface PromotionProductRow {
  id: number;
  description: string;
  code?: string;
}

@Component({
  selector: 'app-create-promotion',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    CheckboxModule,
    SelectModule,
    InputNumberModule,
    DatePickerModule,
    ButtonModule,
    AutoCompleteModule
  ],
  templateUrl: './create-promotion.component.html'
})
export class CreatePromotionComponent implements OnInit {
  id: number | null = null;
  form!: FormGroup;

  groups: Group[] = [];
  discountTypeOptions = [
    { label: 'Percentual (%)', value: 0 },
    { label: 'Valor fixo por unidade (R$)', value: 1 }
  ];

  addedProducts: PromotionProductRow[] = [];
  productPick: Product | null = null;
  productSuggestions: Product[] = [];

  constructor(
    private fb: FormBuilder,
    private promotionService: PromotionService,
    private groupService: GroupService,
    private productService: ProductService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  get pageTitle(): string {
    return this.id ? 'Editar promoção' : 'Nova promoção';
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.id = idParam ? parseInt(idParam, 10) : null;

    this.form = this.fb.group({
      description: [''],
      active: [true],
      validUntil: [null as Date | null, Validators.required],
      discountType: [0, Validators.required],
      discountValue: [0, [Validators.required, Validators.min(0)]],
      groupId: [null as number | null]
    });

    this.groupService.getAll('', null, false, null).subscribe({
      next: res => (this.groups = res.data ?? [])
    });

    if (this.id) {
      this.load();
    }
  }

  goBack(): void {
    this.router.navigate(['/app/promotions']);
  }

  private load(): void {
    this.promotionService.getById(this.id as number).subscribe({
      next: res => {
        const p = res.data;
        if (!p) {
          return;
        }
        let validUntil: Date | null = null;
        if (typeof p.validUntil === 'string' && p.validUntil.length >= 10) {
          const d = new Date(p.validUntil.substring(0, 10) + 'T12:00:00');
          validUntil = isNaN(d.getTime()) ? null : d;
        }
        this.form.patchValue({
          description: p.description ?? '',
          active: p.active,
          validUntil,
          discountType: p.discountType,
          discountValue: p.discountValue,
          groupId: p.groupId ?? null
        });

        const ids = (p.promotionProducts ?? []).map(x => x.productId);
        if (ids.length === 0) {
          this.addedProducts = [];
          return;
        }
        forkJoin(
          ids.map(pid =>
            this.productService.getById(pid).pipe(
              map(r => ({
                id: r.data.id,
                description: r.data.description,
                code: r.data.code
              })),
              catchError(() =>
                of({
                  id: pid,
                  description: `Produto #${pid}`,
                  code: ''
                })
              )
            )
          )
        ).subscribe(rows => (this.addedProducts = rows));
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  onProductSearch(event: AutoCompleteCompleteEvent): void {
    const q = (event.query ?? '').trim();
    this.productService.getAll(q, true, false, false, 1).subscribe({
      next: res => (this.productSuggestions = res.data ?? [])
    });
  }

  onProductSelected(event: AutoCompleteSelectEvent): void {
    const p = event.value as Product | undefined;
    if (!p?.id) {
      return;
    }
    if (!this.addedProducts.some(x => x.id === p.id)) {
      this.addedProducts = [
        ...this.addedProducts,
        { id: p.id, description: p.description, code: p.code ?? undefined }
      ];
    }
    this.productPick = null;
    this.productSuggestions = [];
  }

  removeProduct(productId: number): void {
    this.addedProducts = this.addedProducts.filter(x => x.id !== productId);
  }

  private formatValidUntil(v: unknown): string {
    if (v instanceof Date) {
      const y = v.getFullYear();
      const m = String(v.getMonth() + 1).padStart(2, '0');
      const d = String(v.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    if (typeof v === 'string' && v.length >= 10) {
      return v.substring(0, 10);
    }
    return '';
  }

  productRowLabel(row: PromotionProductRow): string {
    const code = row.code ? ` (${row.code})` : '';
    return `${row.description}${code}`;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
      return;
    }

    const v = this.form.getRawValue();
    const productIds = this.addedProducts.map(x => x.id);
    const groupId = v.groupId != null && v.groupId !== undefined ? Number(v.groupId) : null;
    const gid = groupId !== null && !Number.isNaN(groupId) && groupId > 0 ? groupId : null;

    if (!gid && productIds.length === 0) {
      alert('Selecione um grupo e/ou adicione ao menos um produto à promoção.');
      return;
    }

    const validUntil = this.formatValidUntil(v.validUntil);
    if (!validUntil) {
      alert('Informe a data de validade.');
      return;
    }

    const payload: SavePromotionPayload = {
      description: v.description || null,
      active: !!v.active,
      validUntil,
      discountType: Number(v.discountType),
      discountValue: Number(v.discountValue),
      groupId: gid,
      productIds
    };

    if (this.id) {
      this.promotionService.update(this.id, payload).subscribe({
        next: res => {
          if (res.success !== false) {
            this.router.navigate(['/app/promotions']);
          }
        }
      });
    } else {
      this.promotionService.create(payload).subscribe({
        next: res => {
          if (res.success !== false) {
            this.router.navigate(['/app/promotions']);
          }
        }
      });
    }
  }
}
